import { onAuthStateChanged } from "@firebase/auth";
import imageCompression from "browser-image-compression";
import {
  type ActionCodeSettings,
  deleteUser,
  fetchSignInMethodsForEmail,
  getAuth,
  getIdTokenResult,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  type User,
} from "firebase/auth";
import {
  addDoc,
  collection,
  type DocumentData,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore/lite";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
// import { Loader } from "senf-atomic-design-system";
import type { FormikValues } from "formik";
import React, { useEffect } from "react";
import {
  isChrome,
  isDesktop,
  isFirefox,
  isMobileOnly,
  isTablet,
} from "react-device-detect";
import { generateErrorMessage } from "../firebase/generateErrorMessage";
import { upsertUserDocAcrossRegions } from "../firebase/upsertUserDocAcrossRegions";
import { useCreateUserWithEmailAndPassword } from "../firebase/useCreateUserWithEmailAndPassword";
import { useHandleSubmitEditDetails } from "../firebase/useHandleSubmitEditDetails";
import { useSignIn } from "../firebase/useSignInWithPopup";
import type { Project } from "../types";
import { ifAllUserDetailsAreFilled } from "../utils/ifAllUserDetailsAreFilled";
import { findInRegions } from "../utils/multiRegionQuery";
import { auth, db, runPreSignOutCallbacks, storage } from "./firebase";
import {
  deleteComment,
  getUserComments,
  getUserIdeas,
  getUserLikes,
  getUserOrganizations,
  getUserSurveyEntries,
  hideIdea,
  submitComment,
  unHideIdea,
} from "./providerFunctions";

export interface AuthState {
  user: (DocumentData & User) | null;
  setUser: (user: (DocumentData & User) | null) => void;
  loading: boolean | string;
  setLoading: (loading: boolean | string) => void;
  errorMessage: string;
}
export interface AuthMethods {
  signIn: (data: {
    provider?: string;
    formikStore?: FormikValues;
    id?: string;
    project?: Project;
  }) => Promise<User | DocumentData>;
  signOut: () => Promise<void>;
  deleteUserFromDb: (
    userId: string,
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { error: string; success?: undefined }
  >;
  createUser: (formikStore: FormikValues) => Promise<User | DocumentData>;
  sendPasswordResetEmail: (
    email: string,
    actionCodeSettings?: ActionCodeSettings | undefined,
  ) => Promise<void>;
  sendEmailVerification: typeof sendEmailVerification;
  userExists: (formikStore: FormikValues, login?: boolean) => Promise<string[]>;
  submitEditDetails: (
    formikStore: FormikValues,
  ) => Promise<User | DocumentData>;
  handleImageUpload: (event: Event, user?: DocumentData) => Promise<string>;
  ifAllUserDetailsAreFilled: (user?: DocumentData) => boolean;

  // do we need a separate context for these methods?
  getUserData: (
    userId: string,
    project?: Project,
    participant?: DocumentData,
  ) => Promise<DocumentData>;
  getUserIdeas: (project: any, userId: string) => Promise<DocumentData[]>;
  getUserOrganizations: (
    userId: string,
    isSuperAdmin: boolean,
  ) => Promise<DocumentData[]>;
  submitComment: (
    ideaId: string,
    commentData: { body: string },
    user: User,
    device: "mobile" | "tablet" | "desktop" | "other",
  ) => Promise<{
    commentId: string | null;
    title: string | null;
    createdAt: string;
    ideaId: string;
    userHandle: string | null;
    photoURL: string | null;
    userId: string;
    gender: string | null;
    age: number | null;
    topic: string;
  }>;
  deleteComment: (
    commentId: string,
    user: User,
    ideaId: string,
  ) => Promise<{
    commentId: string;
  }>;
  handleLike: (
    ideaId: string,
  ) => Promise<false | { ideaId: string; userHandle: string; userId: string }>;
}
export interface AuthContext extends AuthState, AuthMethods {}

const AuthContext = React.createContext<AuthContext>({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState<DocumentData | null>(null);
  const blankError = "";
  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState<boolean | string>(true);
  const handleSetErrorMessage = (error: Error) => {
    setErrorMessage(generateErrorMessage(error?.code ?? error?.message));
  };

  useEffect(() => {
    if (user?.userId) {
      if (window.Cypress) {
        window.user = user;
      }
    }
  }, [user, window?.Cypress]);

  // Methods

  /**
   * @params participant this is used for when user data was changed that is otherwise only saved in the project, which has not been updated yet
   */
  const getUserData = async (
    userId: string,
    project?: Project,
    participant?: DocumentData,
  ) => {
    if (!userId) throw new Error("No user id provided to getUserData");
    const auth = getAuth();
    const { currentUser } = auth;

    // Fast path: try the currently active region first.
    // Fallback: scan all regions — handles cold-start when the user's doc lives
    // in a different region than the current default (e.g. US user, EU default).
    let userDoc = (
      await getDocs(
        query(collection(db, "users"), where("userId", "==", userId)),
      )
    ).docs[0];

    if (!userDoc) {
      const found = await findInRegions((regionDb) =>
        query(collection(regionDb, "users"), where("userId", "==", userId)),
      );
      if (found) {
        userDoc = found.docs[0];
      }
    }

    const userData = userDoc?.data();
    if (!userData) {
      const err = new Error("No user data found in getUserData() AuthProvider");
      (err as any).code = "user-doc-not-found";
      throw err;
    }

    // Ensure userId is always set
    userData.userId = userId;

    // Superadmin: same signal as Firestore rules — custom claim `superAdmin` on the
    // ID token (global) OR `users/{uid}.isSuperAdmin` in a regional user doc.
    let superAdminFromClaim = false;
    if (currentUser?.uid === userId) {
      try {
        const { claims } = await getIdTokenResult(currentUser);
        superAdminFromClaim = claims.superAdmin === true;
      } catch {
        // ignore; rely on Firestore field only
      }
    }
    userData.isSuperAdmin =
      superAdminFromClaim || userData.isSuperAdmin === true;

    userData.likes = [];
    userData.comments = [];
    userData.surveyEntries = [];
    userData.organizations = [];

    if (project?.organizationId) {
      const memberRef = doc(
        db,
        `organizations/${project.organizationId}/members/${userId}`,
      );
      const memberDoc = await getDoc(memberRef);
      const memberData = memberDoc.data();

      const projectMemberRef = doc(
        db,
        `organizations/${project.organizationId}/projects/${project.projectId}/members/${userId}`,
      );
      const projectMemberDoc = await getDoc(projectMemberRef);
      const isProjectMember = projectMemberDoc.exists();

      // Set admin status based on organization role
      userData.isAdmin =
        memberData?.role === "admin" ||
        memberData?.role === "editor" ||
        isProjectMember ||
        (memberData && !project?.settings?.accessLimitations) ||
        userData?.isSuperAdmin;
    }

    const isEmailVerified = () => {
      // https://stackoverflow.com/questions/38398545/firebase-facebook-auth-email-verified-always-false
      // thats why I am setting it to true if the user is logged in with facebook
      if (currentUser?.providerData[0]?.providerId === "facebook.com") {
        return true;
      }
      return currentUser?.emailVerified;
    };
    if (currentUser?.uid === userId) {
      // attach these properties to logged in user
      userData.profileCompleted = ifAllUserDetailsAreFilled(
        userData,
        participant,
        project,
      );
      userData.email = currentUser?.email;
      userData.emailVerified = !!isEmailVerified();
      userData.authenticated = !!isEmailVerified() || currentUser.isAnonymous;

      const results = await Promise.allSettled([
        getUserLikes(userId),
        getUserComments(userId),
        getUserSurveyEntries(userId),
        getUserOrganizations(userId, userData.isSuperAdmin),
      ]);

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(
            `Promise at index ${index} was rejected with reason:`,
            result.reason,
          );
        }
      });

      const [likes, comments, surveyEntries, organizations] = results.map(
        (result) => (result.status === "fulfilled" ? result.value : []),
      );

      userData.likes = likes;
      userData.comments = comments;
      userData.surveyEntries = surveyEntries;
      userData.organizations = organizations;
    }
    return userData;
  };

  const sendVerification = {
    sendEmailVerification: false,
    emailVerificationOptions: {
      // url: `${window.location.protocol}//${window.location.host}/verify`,
      url: window.location.href,
    },
  };
  const signInWith = useSignIn(auth, db);
  const createUserWithEmailAndPassword = useCreateUserWithEmailAndPassword(
    auth,
    db,
    sendVerification,
  );
  const submitEditDetails = useHandleSubmitEditDetails(user || {});

  // Handler
  const handler: AuthMethods = {
    signIn({ provider, formikStore, id, project }) {
      if (!formikStore) throw new Error("No formikStore provided to signIn()");
      setErrorMessage(blankError);
      return new Promise((resolve, reject) => {
        switch (provider) {
          case "apple":
          case "facebook":
          case "google":
            setLoading(id || provider);
            signInWith
              .provider(provider, ["email"], formikStore)
              // Return the inner promise so the outer .finally(setLoading(false))
              // waits until setUser has actually run. Without `return` the outer
              // chain resolves the moment getUserData is *started*, which leaves
              // a brief window of `loading=false, user=null` and causes auth
              // drawers gated on that state to flash open.
              .then((user) =>
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    handleSetErrorMessage(error);
                    reject(error);
                  }),
              )
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;
          case "email":
            setLoading("email");
            signInWith
              .email(formikStore)
              .then((user) =>
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    if ((error as any)?.code !== "user-doc-not-found") {
                      handleSetErrorMessage(error);
                    }
                    reject(error);
                  }),
              )
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;
          case "anonymous":
            setLoading("anonymous");
            signInWith
              .anonymous(formikStore)
              .then((user) =>
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    handleSetErrorMessage(error);
                    reject(error);
                  }),
              )
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;

          default:
            throw new Error("No provider provided to signIn");
        }
      });
    },
    signOut: async () => {
      runPreSignOutCallbacks();
      await signOut(auth);
      setUser(null);
      setLoading(false);
    },
    deleteUserFromDb: async () => {
      const currentuser = auth.currentUser;

      if (!currentuser) throw Error("User is not logged in");

      try {
        const cantDeleteSuperAdmins = "dein@senf.koeln";
        if (cantDeleteSuperAdmins === currentuser.email) {
          throw new Error("Can't delete SuperAdmin");
        }

        await deleteUser(currentuser);
        return { success: true };
      } catch (error) {
        if (error.code === "auth/requires-recent-login") {
          const error = "auth/requires-recent-login";
          return { error };
        }
        throw new Error(error, "error in deleteUserFromDb");
      }
    },
    getUserData,
    getUserIdeas,
    getUserOrganizations,
    getUserSurveyEntries,
    hideIdea,
    unHideIdea,
    async submitComment(...props) {
      const comment = await submitComment(...props);
      setUser((prevUser) => {
        if (!prevUser)
          throw new Error(
            "No user found in  auth state trying to submit comment",
          );
        return { ...prevUser, comments: [...prevUser.comments, comment] };
      });

      return comment;
    },
    async deleteComment(...props) {
      const deletedCommentId = await deleteComment(...props);
      const commentIndex = user?.comments?.findIndex(
        ({ commentId }: { commentId: string }) =>
          commentId === deletedCommentId,
      );
      if (commentIndex > -1) {
        setUser((prevUser) => {
          if (!prevUser)
            throw new Error(
              "No user found in  auth state trying to delete comment",
            );
          const updatedComments = [...prevUser.comments];
          updatedComments.splice(commentIndex, 1);
          return { ...prevUser, comments: updatedComments };
        });
      }
      return deletedCommentId;
    },
    async handleLike(ideaId: string) {
      return new Promise((resolve) => {
        const ideaIndex = user?.likes?.findIndex(
          (like) => like.ideaId === ideaId,
        );
        if (ideaIndex > -1) {
          setUser((prevUser) => {
            if (!prevUser)
              throw new Error(
                "No user found in  auth state trying to delete like",
              );
            const updatedLikes = [...prevUser.likes];
            updatedLikes.splice(ideaIndex, 1);
            return { ...prevUser, likes: updatedLikes };
          });
          resolve(false);
          return;
        }
        const newLike = {
          ideaId,
          userHandle: user?.handle,
          userId: user?.userId,
        };
        setUser((prevUser) => {
          if (!prevUser)
            throw new Error("No user found in  auth state trying to add like");
          return { ...prevUser, likes: [...prevUser.likes, newLike] };
        });
        resolve(newLike);
      });
    },
    async createUser(formikRegisterStore) {
      return new Promise((resolve, reject) => {
        setLoading("email");
        createUserWithEmailAndPassword(formikRegisterStore)
          .then(({ user }) => {
            getUserData(user?.uid).then((userData) => {
              setUser(userData);
              resolve(userData);
            });
          })
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },

    async sendPasswordResetEmail(email, actionCodeSettings) {
      return new Promise((resolve, reject) => {
        setLoading("reset");
        sendPasswordResetEmail(auth, email, actionCodeSettings)
          .then(resolve)
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },
    sendEmailVerification,
    async userExists(formikStore, login = true) {
      setLoading("email");
      try {
        const { email } = formikStore.values;
        const signInMethod = await fetchSignInMethodsForEmail(auth, email);
        if (!login) return signInMethod;
        if (signInMethod?.includes("google.com"))
          handler.signIn({ provider: "google", formikStore });
        if (signInMethod?.includes("facebook.com"))
          handler.signIn({ provider: "facebook", formikStore }); // do we need to return here too ?
        return signInMethod;
      } catch (err) {
        handleSetErrorMessage(err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    submitEditDetails: async ({ formikStore, project }) => {
      return new Promise((resolve, reject) => {
        setLoading("edit");
        submitEditDetails(formikStore, project)
          .then((participant) => {
            getUserData(participant?.userId, project, participant).then(
              (userData) => {
                setUser(userData);
                resolve(userData);
              },
            );
          })
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },
    handleImageUpload: async (event, targetUser) => {
      const currentUser =
        !targetUser && auth.currentUser ? auth.currentUser : targetUser;
      if (!currentUser) throw Error("User is not logged in");
      if (
        auth.currentUser?.uid !== currentUser.uid &&
        currentUser.isAdmin === false &&
        currentUser.isSuperAdmin === false &&
        currentUser.isModerator === false
      ) {
        throw new Error("user not authorized to handleImageUpload");
      }
      const target = event?.target as HTMLInputElement;
      if (!target?.files?.length) {
        throw new Error("no file selected");
      }
      const imageFile = target.files[0];

      const options = {
        maxSizeMB: 0.03,
        maxWidthOrHeight: 700,
        useWebWorker: true,
        fileType: "image/jpeg",
      };
      try {
        const compressedFile = await imageCompression(imageFile, options);
        const storageRef = ref(
          storage,
          `profileImages/${currentUser.uid}/thumbnail`,
        );
        await uploadBytes(storageRef, compressedFile);
        const photoURL = await getDownloadURL(storageRef);
        await upsertUserDocAcrossRegions(currentUser.uid, { photoURL });
        return photoURL;
      } catch (error) {
        throw new Error(error);
      }
    },
  };

  const logUserRetention = async (user: User) => {
    if (!user) return;
    try {
      // A user's profile doc lives in whatever region they first signed in to
      // (EU → (default) db, US → us-data). Reading from only the active db
      // would silently miss any cross-region user and skip their retention entry.
      // findInRegions checks all dbs in parallel and returns the first hit.
      const found = await findInRegions((regionDb) =>
        query(collection(regionDb, "users"), where("userId", "==", user.uid)),
      );
      if (!found || found.docs.length === 0) return;

      const homeDoc = found.docs[0];
      const { projectId } = homeDoc.data();
      // Write the activity entry to the same db where the user's doc lives so
      // activityLogbook subcollection stays collocated with their profile.
      const homeDb = homeDoc.ref.firestore;
      const now = new Date().getTime();

      const visitHistoryRef = collection(
        homeDb,
        "users",
        user.uid,
        "activityLogbook",
      );

      // Ensure URL doesn't exceed Firestore limits (1,500 bytes for indexed fields)
      const currentUrl = window.location.href;
      const truncatedUrl =
        currentUrl.length > 1500 ? currentUrl.substring(0, 1500) : currentUrl;

      const visitData = {
        userId: user.uid,
        visitorId: localStorage.getItem("visitorId") || null,
        projectId: projectId || null,
        timeStamp: now,
        createdAt: new Date().toISOString(),
        url: truncatedUrl,
        referrer: localStorage.getItem("referrer") || null,
        device: isMobileOnly
          ? "mobile"
          : isTablet
            ? "tablet"
            : isDesktop
              ? "desktop"
              : "other",
      };
      await addDoc(visitHistoryRef, visitData);
    } catch (error) {
      // Log error but don't throw - this shouldn't break the auth flow
      console.warn("Failed to log user retention:", error);
      // Optionally, you could implement retry logic or fallback storage here
    }
  };

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      // Each time onAuthStateChanged fires we flip cancelled so any in-flight
      // retry loop from a previous callback stops writing to state.
      cancelled = false;

      if (!authUser) {
        if (cancelled) return;
        setUser(null);
        setLoading(false);
        return;
      }

      // Until getUserData finishes, context `user` is still null. Without
      // loading=true, the app can treat this as logged-out and redirect to
      // /login (e.g. right after email magic-link sign-in on a deep link).
      setLoading(true);

      // Retry mechanism for newly created users whose Firestore doc may not
      // have propagated yet (typical for first-time magic-link sign-in: the
      // doc is written by LoggingInModal *after* onAuthStateChanged fires).
      // The cancelled flag prevents a stale loop (e.g. from a previous
      // sign-in) from overwriting state after sign-out.
      const retryGetUserData = async (maxRetries = 10, delay = 300) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          if (cancelled) return;
          try {
            const userData = await getUserData(authUser.uid);
            if (!cancelled) setUser(userData);
            return;
          } catch (error) {
            const isLastAttempt = attempt >= maxRetries;
            if (isLastAttempt) {
              if (!cancelled) setUser(null);
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      };

      retryGetUserData().finally(() => {
        if (!cancelled) setLoading(false);
      });

      // Log user retention asynchronously without blocking auth flow
      logUserRetention(authUser).catch((error) => {
        console.warn("User retention logging failed:", error);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    const timout = setTimeout(() => setErrorMessage(blankError), 20000);
    return () => clearTimeout(timout);
  }, [errorMessage]);

  // if (loading) {
  //   return (
  //     <div className="flex justify-center items-center flex-col h-full w-full">
  //       <div style={{ width: "200px" }}>
  //         <Loader />
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        setLoading,
        errorMessage,
        ...handler,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => React.useContext(AuthContext);
export default AuthProvider;
